import React, { useEffect, useRef, useState } from 'react';

const SimpleCameraGestures = ({ onGesture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [status, setStatus] = useState('Initializing...');
  const lastGestureTime = useRef(0);

  useEffect(() => {
    let currentStream = null;

    const initializeCamera = async () => {
      try {
        // Request camera access
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            width: 320, 
            height: 240,
            facingMode: 'user'
          }
        });

        currentStream = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
          setIsActive(true);
          setStatus('Camera active - Use keyboard/mouse controls');
        }
      } catch (error) {
        console.error('Camera access error:', error);
        setStatus('Camera unavailable - Use keyboard/mouse controls');
      }
    };

    initializeCamera();

    // Cleanup
    return () => {
      if (currentStream) {
        const tracks = currentStream.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, []);

  // Simple gesture simulation based on video activity (placeholder)
  useEffect(() => {
    if (!isActive) return;

    let gestureSequence = 0;
    const gestures = ['swipe_left', 'swipe_right', 'point'];

    const handleVideoClick = () => {
      const now = Date.now();
      if (now - lastGestureTime.current > 1000) {
        lastGestureTime.current = now;
        // Cycle through different gestures for testing
        const gesture = gestures[gestureSequence % gestures.length];
        gestureSequence++;
        console.log('Simulated gesture:', gesture);
        onGesture?.(gesture);
        
        // Visual feedback
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = 'rgba(76, 175, 80, 0.8)';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          setTimeout(() => {
            if (window.drawSimpleGestureStatus) {
              window.drawSimpleGestureStatus();
            }
          }, 200);
        }
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        onGesture?.('swipe_right'); // Right swipe = previous
      } else if (e.key === 'ArrowRight') {
        onGesture?.('swipe_left'); // Left swipe = next
      } else if (e.key === ' ' || e.key === 'Enter') {
        onGesture?.('point');
      }
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('click', handleVideoClick);
      document.addEventListener('keydown', handleKeyPress);
      return () => {
        canvas.removeEventListener('click', handleVideoClick);
        document.removeEventListener('keydown', handleKeyPress);
      };
    }
  }, [isActive, onGesture]);

  // Draw status on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const drawStatus = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw semi-transparent overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw status text
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.font = '11px Arial';
      ctx.textAlign = 'center';
      
      ctx.fillText('Simple Gestures', canvas.width / 2, canvas.height / 2 - 20);
      ctx.fillText('Click to simulate gesture', canvas.width / 2, canvas.height / 2 - 5);
      ctx.fillText('Arrow keys also work', canvas.width / 2, canvas.height / 2 + 10);

      if (isActive) {
        ctx.fillStyle = 'rgba(76, 175, 80, 0.8)';
        ctx.fillRect(canvas.width - 20, 10, 10, 10);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.font = '8px Arial';
        ctx.fillText('LIVE', canvas.width - 15, 30);
      }
    };

    // Make drawStatus available to other functions
    window.drawSimpleGestureStatus = drawStatus;

    drawStatus();
    
    // Update periodically
    const interval = setInterval(drawStatus, 2000);
    return () => {
      clearInterval(interval);
      window.drawSimpleGestureStatus = null;
    };
  }, [status, isActive]);

  return (
    <div className="simple-camera-gestures">
      <video
        ref={videoRef}
        style={{ display: 'none' }}
        autoPlay
        playsInline
        muted
      />
      <canvas
        ref={canvasRef}
        width={200}
        height={150}
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          border: '2px solid rgba(100, 181, 246, 0.3)',
          borderRadius: '12px',
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000,
          cursor: isActive ? 'pointer' : 'default'
        }}
        title={isActive ? 'Click to simulate gesture' : 'Camera initializing...'}
      />
    </div>
  );
};

export default SimpleCameraGestures;